import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { BarChart3, Clock, TrendingUp, Wrench, Loader2, Users, Plus, Edit2, Trash2, Save, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";

function fmtChf(n) {
  if (n == null) return "—";
  return n.toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtH(n) {
  if (n == null) return "—";
  return `${(n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 1, maximumFractionDigits: 2 })} h`;
}
function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
}

const PERIOD_LABELS = { jour: "Aujourd'hui", semaine: "Cette semaine", mois: "Ce mois" };

export default function AtelierStats() {
  const [period, setPeriod] = useState("mois");
  const [mecanicienId, setMecanicienId] = useState("_all_");
  const [mecaniciens, setMecaniciens] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter55 — Modal drill-down liste des OR (clic sur le nombre)
  const [orModal, setOrModal] = useState({ open: false, title: "", ids: [], items: [], loading: false });

  const openOrModal = async (title, ids) => {
    if (!ids || ids.length === 0) {
      toast.info("Aucun OR à afficher sur cette ligne.");
      return;
    }
    setOrModal({ open: true, title, ids, items: [], loading: true });
    try {
      const { data: resp } = await api.post("/atelier/or-details", { ids });
      setOrModal((s) => ({ ...s, items: resp.items || [], loading: false }));
    } catch (e) {
      toast.error("Erreur chargement des OR");
      setOrModal((s) => ({ ...s, loading: false }));
    }
  };
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customDates, setCustomDates] = useState(false);

  useEffect(() => {
    api.get("/atelier/mecaniciens")
      .then(({ data }) => setMecaniciens(data || []))
      .catch(() => setMecaniciens([]));
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (customDates && dateFrom && dateTo) {
        params.date_from = dateFrom;
        params.date_to = dateTo;
      } else {
        params.period = period;
      }
      if (mecanicienId && mecanicienId !== "_all_") params.mecanicien_id = mecanicienId;
      const { data } = await api.get("/atelier/stats", { params });
      setData(data);
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  }, [period, mecanicienId, customDates, dateFrom, dateTo]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const total = data?.total || { nb_or: 0, heures: 0, ca_ht: 0 };
  const tauxMoyen = total.heures > 0 ? total.ca_ht / total.heures : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="atelier-stats-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-lg bg-[#52a675]/10 text-[#52a675] flex items-center justify-center">
          <BarChart3 className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">Statistiques atelier</h1>
          <p className="text-sm text-gray-500">Productivité et CA par mécanicien</p>
        </div>
      </div>

      {/* Filtres */}
      <Card className="p-4 mb-4 border-gray-200">
        <div className="flex flex-wrap items-end gap-3">
          <Tabs value={customDates ? "_custom_" : period} onValueChange={(v) => {
            if (v === "_custom_") { setCustomDates(true); }
            else { setCustomDates(false); setPeriod(v); }
          }}>
            <TabsList>
              <TabsTrigger value="jour" data-testid="stats-tab-jour">Jour</TabsTrigger>
              <TabsTrigger value="semaine" data-testid="stats-tab-semaine">Semaine</TabsTrigger>
              <TabsTrigger value="mois" data-testid="stats-tab-mois">Mois</TabsTrigger>
              <TabsTrigger value="_custom_" data-testid="stats-tab-custom">Période</TabsTrigger>
            </TabsList>
          </Tabs>

          {customDates && (
            <>
              <div>
                <Label className="text-xs">Du</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="stats-date-from" />
              </div>
              <div>
                <Label className="text-xs">Au</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="stats-date-to" />
              </div>
            </>
          )}

          <div className="min-w-[200px]">
            <Label className="text-xs">Mécanicien</Label>
            <Select value={mecanicienId} onValueChange={setMecanicienId}>
              <SelectTrigger className="h-9" data-testid="stats-mecanicien">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">Tous</SelectItem>
                {mecaniciens.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {data?.date_from && (
            <div className="text-xs text-gray-500 ml-auto">
              Période analysée : <b>{fmtDate(data.date_from)}</b> → <b>{fmtDate(data.date_to)}</b>
            </div>
          )}
        </div>
      </Card>

      {/* KPI cards globaux */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 border-gray-200">
          <div className="flex items-center gap-2 text-xs uppercase text-gray-500 mb-1">
            <Wrench className="w-3.5 h-3.5" /> OR finalisés
          </div>
          <button
            type="button"
            onClick={() => openOrModal("Tous les OR finalisés", total.or_ids || [])}
            className="text-2xl font-bold font-mono text-left hover:text-emerald-700 hover:underline disabled:no-underline"
            disabled={!total.nb_or}
            data-testid="stats-nb-or-total"
          >
            {total.nb_or}
          </button>
        </Card>
        <Card className="p-4 border-gray-200">
          <div className="flex items-center gap-2 text-xs uppercase text-gray-500 mb-1">
            <Clock className="w-3.5 h-3.5" /> Heures réelles
          </div>
          <div className="text-2xl font-bold font-mono text-amber-700">{fmtH(total.heures)}</div>
        </Card>
        <Card className="p-4 border-gray-200">
          <div className="flex items-center gap-2 text-xs uppercase text-gray-500 mb-1">
            <TrendingUp className="w-3.5 h-3.5" /> CA prestations (3400)
          </div>
          <div className="text-2xl font-bold font-mono text-emerald-700">{fmtChf(total.ca_ht)} <span className="text-sm font-normal text-gray-500">CHF</span></div>
        </Card>
        <Card className="p-4 border-gray-200">
          <div className="flex items-center gap-2 text-xs uppercase text-gray-500 mb-1">
            <BarChart3 className="w-3.5 h-3.5" /> Taux horaire moyen
          </div>
          <div className="text-2xl font-bold font-mono text-indigo-700">{fmtChf(tauxMoyen)} <span className="text-sm font-normal text-gray-500">CHF/h</span></div>
        </Card>
      </div>

      {/* Détail par mécanicien */}
      <Card className="border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-500" />
          <span className="font-semibold text-sm">Détail par mécanicien</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : (data?.by_mecanicien || []).length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            Aucune donnée sur cette période.
            <div className="text-xs mt-2 text-gray-500">
              Astuce : pour qu'un OR remonte ici, il doit avoir un <b>mécanicien</b> et un <b>temps réel</b> renseignés (depuis la fiche OR ou le dialog de finalisation).
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left p-3">Mécanicien</th>
                <th className="text-right p-3">OR</th>
                <th className="text-right p-3">Heures réelles</th>
                <th className="text-right p-3">CA prestations (3400)</th>
                <th className="text-right p-3">CHF/h moyen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.by_mecanicien || []).map((m) => {
                const taux = m.heures > 0 ? m.ca_ht / m.heures : 0;
                return (
                  <tr key={m.mecanicien_id} className="hover:bg-gray-50/50" data-testid={`stats-row-${m.mecanicien_id}`}>
                    <td className="p-3 font-semibold">{m.nom}</td>
                    <td className="p-3 text-right font-mono">
                      <button
                        type="button"
                        onClick={() => openOrModal(`OR de ${m.nom}`, m.or_ids || [])}
                        className="hover:text-emerald-700 hover:underline disabled:no-underline"
                        disabled={!m.nb_or}
                        data-testid={`stats-nb-or-${m.mecanicien_id}`}
                      >
                        {m.nb_or}
                      </button>
                    </td>
                    <td className="p-3 text-right font-mono text-amber-700">{fmtH(m.heures)}</td>
                    <td className="p-3 text-right font-mono text-emerald-700">{fmtChf(m.ca_ht)} CHF</td>
                    <td className="p-3 text-right font-mono text-indigo-700">{fmtChf(taux)} CHF/h</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <div className="mt-4 text-[11px] text-gray-500">
        💡 <b>Période { (customDates ? "personnalisée" : PERIOD_LABELS[period] || period).toLowerCase() } :</b> seuls les OR avec un intervenant assigné <em>et</em> des heures renseignées sont comptés.
        Le <b>CA prestations</b> compte uniquement les lignes ventilées sur le compte <b>3400</b> (main d'œuvre, forfaits, lignes libres marquées "prestations"). Les ventes de pièces (3200) sont exclues car elles ne reflètent pas la productivité atelier.
        La date de référence est la date de facturation (ou de dernière modification si non facturé).
      </div>

      {/* iter55 — Modal drill-down liste des OR */}
      <Dialog open={orModal.open} onOpenChange={(o) => !o && setOrModal({ ...orModal, open: false })}>
        <DialogContent className="max-w-2xl" data-testid="stats-or-modal">
          <DialogHeader>
            <DialogTitle>{orModal.title}</DialogTitle>
            <DialogDescription>{orModal.ids.length} ordre(s) de réparation</DialogDescription>
          </DialogHeader>
          {orModal.loading ? (
            <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500 sticky top-0">
                  <tr>
                    <th className="text-left p-2">N° OR</th>
                    <th className="text-left p-2">Client</th>
                    <th className="text-left p-2">Véhicule</th>
                    <th className="text-right p-2">Total TTC</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orModal.items.map((or) => (
                    <tr key={or.id} className="hover:bg-gray-50/50" data-testid={`or-modal-row-${or.id}`}>
                      <td className="p-2 font-mono font-semibold">{or.numero}</td>
                      <td className="p-2">{or.client_nom_complet || "—"}</td>
                      <td className="p-2 text-gray-600">{or.vehicule_label || "—"}</td>
                      <td className="p-2 text-right font-mono">{fmtChf(or.total_ttc)} CHF</td>
                      <td className="p-2 text-right">
                        <Link
                          to={`/atelier/or/${or.id}`}
                          target="_blank"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition"
                          data-testid={`or-modal-open-${or.id}`}
                        >
                          <ExternalLink className="w-3 h-3" /> Ouvrir
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrModal({ ...orModal, open: false })}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <IntervenantsManager onChanged={fetchStats} />
    </div>
  );
}

/**
 * Section admin (visible uniquement pour role=admin) permettant de gérer le
 * référentiel des intervenants atelier (Jean, Paul, Pierre…). Indépendant
 * des comptes utilisateurs avec login.
 */
function IntervenantsManager({ onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactifs, setShowInactifs] = useState(false);
  const [comptes, setComptes] = useState([]);   // iter273 — comptes rattachables
  const [editingId, setEditingId] = useState(null);
  const [editingNom, setEditingNom] = useState("");
  const [newNom, setNewNom] = useState("");
  const [creating, setCreating] = useState(false);

  const userRole = (() => {
    try { return JSON.parse(localStorage.getItem("cm_user") || "{}").role; }
    catch { return null; }
  })();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/intervenants", { params: { include_inactifs: showInactifs } });
      setItems(data || []);
    } catch (e) {
      // Si non-admin, 403 attendu (la section ne s'affiche pas). Log uniquement les autres erreurs.
      if (e.response?.status !== 403) console.error("fetch intervenants failed:", e);
    } finally { setLoading(false); }
  }, [showInactifs]);

  useEffect(() => {
    if (userRole === "admin" || userRole === "comptoir" || userRole === "compta") fetchItems();
    else setLoading(false);
  }, [fetchItems, userRole]);

  useEffect(() => {
    if (userRole !== "admin") return;
    api.get("/auth/users")
      .then(({ data }) => setComptes((data || []).filter((u) => ["atelier", "admin"].includes(u.role))))
      .catch(() => setComptes([]));
  }, [userRole]);

  if (userRole !== "admin") return null;

  const handleCreate = async () => {
    if (!newNom.trim()) { toast.error("Saisissez un nom"); return; }
    setCreating(true);
    try {
      await api.post("/intervenants", { nom: newNom.trim() });
      toast.success(`Intervenant « ${newNom.trim()} » créé`);
      setNewNom("");
      await fetchItems();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de création");
    } finally { setCreating(false); }
  };

  const handleSaveEdit = async (id) => {
    if (!editingNom.trim()) { toast.error("Le nom est obligatoire"); return; }
    try {
      await api.patch(`/intervenants/${id}`, { nom: editingNom.trim() });
      toast.success("Intervenant renommé");
      setEditingId(null); setEditingNom("");
      await fetchItems();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de mise à jour");
    }
  };

  // iter273 — Rattacher la fiche intervenant à un compte : c'est ce lien qui fait
  // fonctionner la vue « Mes OR » du mécano quand les noms ne correspondent pas.
  const handleLink = async (it, userId) => {
    try {
      await api.patch(`/intervenants/${it.id}`, { linked_user_id: userId });
      toast.success(userId ? "Compte rattaché" : "Rattachement retiré");
      await fetchItems();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rattachement impossible");
    }
  };

  const handleToggle = async (it) => {
    try {
      await api.patch(`/intervenants/${it.id}`, { actif: !it.actif });
      toast.success(it.actif ? `${it.nom} désactivé` : `${it.nom} réactivé`);
      await fetchItems();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (it) => {
    const warning = it.usage_count > 0
      ? `Cet intervenant est référencé dans ${it.usage_count} OR. La suppression sera douce (désactivé, l'historique reste intact).\n\nConfirmer ?`
      : `Supprimer définitivement « ${it.nom} » ?`;
    if (!window.confirm(warning)) return;
    try {
      const { data } = await api.delete(`/intervenants/${it.id}`);
      toast.success(data.soft_delete ? `${it.nom} désactivé (historique conservé)` : `${it.nom} supprimé`);
      await fetchItems();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Card className="mt-6 border-gray-200 overflow-hidden" data-testid="intervenants-manager">
      <div className="px-4 py-3 border-b border-gray-100 bg-amber-50/50 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-amber-700" />
          <span className="font-semibold text-sm text-amber-900">Gérer les intervenants atelier</span>
          <Badge variant="outline" className="text-[10px]">Admin</Badge>
        </div>
        <label className="text-xs text-gray-600 flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactifs}
            onChange={(e) => setShowInactifs(e.target.checked)}
            data-testid="intervenant-show-inactifs"
          />
          Afficher les inactifs
        </label>
      </div>

      <div className="p-4 space-y-3">
        {/* Création */}
        <div className="flex gap-2">
          <Input
            value={newNom}
            onChange={(e) => setNewNom(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
            placeholder="Nom du nouvel intervenant (ex: Jean Dupont)"
            className="flex-1"
            data-testid="intervenant-new-nom"
          />
          <Button onClick={handleCreate} disabled={creating || !newNom.trim()} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="intervenant-create-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Ajouter
          </Button>
        </div>

        {/* Liste */}
        <div className="border border-gray-100 rounded divide-y divide-gray-100 bg-white">
          {loading ? (
            <div className="p-6 text-center text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              Aucun intervenant. Ajoutez vos mécaniciens ci-dessus.
              <div className="text-[10px] mt-1 text-gray-500">
                Par défaut, le système utilise les comptes utilisateurs (Atelier, Administrateur). Dès que vous créez un intervenant ici, c'est lui qui est utilisé partout.
              </div>
            </div>
          ) : (
            items.map((it) => (
              <div key={it.id} className="p-3 flex items-center gap-2 flex-wrap" data-testid={`intervenant-row-${it.id}`}>
                {editingId === it.id ? (
                  <>
                    <Input
                      value={editingNom}
                      onChange={(e) => setEditingNom(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleSaveEdit(it.id); if (e.key === "Escape") { setEditingId(null); setEditingNom(""); } }}
                      className="flex-1"
                      autoFocus
                      data-testid={`intervenant-edit-input-${it.id}`}
                    />
                    <Button size="sm" onClick={() => handleSaveEdit(it.id)} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid={`intervenant-save-${it.id}`}>
                      <Save className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditingNom(""); }}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 flex items-center gap-2 flex-wrap">
                      <span className={`font-medium ${it.actif ? "text-gray-900" : "text-gray-400 line-through"}`}>{it.nom}</span>
                      {!it.actif && <Badge variant="outline" className="text-[10px] bg-gray-100 text-gray-500">Inactif</Badge>}
                      {it.usage_count > 0 && (
                        <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                          {it.usage_count} OR
                        </Badge>
                      )}
                      <select
                        value={it.linked_user_id || ""}
                        onChange={(e) => handleLink(it, e.target.value)}
                        className="text-[11px] border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-700"
                        title="Compte utilisateur de ce mécano (vue « Mes OR »)"
                        data-testid={`intervenant-compte-${it.id}`}
                      >
                        <option value="">— Aucun compte rattaché —</option>
                        {comptes.map((u) => (
                          <option key={u.id} value={u.id}>
                            {(u.prenom ? `${u.prenom} ${u.nom || ""}`.trim() : u.nom) || u.login}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingId(it.id); setEditingNom(it.nom); }} title="Renommer" data-testid={`intervenant-edit-${it.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleToggle(it)} title={it.actif ? "Désactiver" : "Réactiver"} data-testid={`intervenant-toggle-${it.id}`}>
                      {it.actif ? <span className="text-xs text-gray-500">Désactiver</span> : <span className="text-xs text-emerald-700">Réactiver</span>}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(it)} className="text-red-600 hover:bg-red-50" title="Supprimer" data-testid={`intervenant-delete-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
        <div className="text-[10px] text-gray-500">
          💡 Rattachez un compte à l&apos;intervenant pour que le mécano retrouve ses dossiers dans sa vue « Mes OR ».
          Sinon ce référentiel sert <b>aux statistiques</b>.
          Un intervenant référencé dans un OR ne peut pas être supprimé définitivement (désactivation au lieu de suppression, pour préserver l'historique).
        </div>
      </div>
    </Card>
  );
}
